import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  LuCheck,
  LuChevronDown,
  LuChevronLeft,
  LuChevronRight,
  LuChevronsLeft,
  LuChevronsRight,
  LuDownload,
  LuLightbulb,
  LuLock,
  LuMessageSquare,
  LuNotebookPen,
  LuPlay,
  LuSparkles,
  LuStar,
  LuTrendingDown,
  LuTrendingUp,
  LuTrophy,
  LuVideo,
  LuX,
} from "react-icons/lu";
import { Btn, Badge, Field, Spinner, Skel, Empty, Note, Dialog } from "../components/Ui";
import {
  setQuestionAnswer,
  setDocId,
  setAnswers,
  setShowPro,
  setScenarioBased,
  setInterviewLevel,
  setIndustryInterview,
} from "../../store/actions/actions";
import "./InterviewPrep.css";

/* Endpoints — identical to legacy InterviewPrep.js */
const IP_API = "https://us-east1-foursssolutions.cloudfunctions.net/interview_prep_function_v2";
const FEEDBACK_API = "https://aspire-quest-interview-tool-v2-980069659423.us-east1.run.app";
const FEEDBACK_DOC_API = "https://aspire-quest-interview-tool-doc-v2-980069659423.us-east1.run.app";
const RECORDS_PER_PAGE = 5;

const LEVELS = ["beginner", "intermediate", "advanced"];

/* ---------- helpers carried over from legacy ---------- */
function getInterviewDate(interview) {
  if (interview.type === "video") {
    return interview.date ? new Date(interview.date) : new Date(0);
  }
  if (interview.isCompleted) {
    return interview.datetime ? new Date(interview.datetime) : new Date(0);
  }
  if (interview.data) {
    return interview.data.datetime
      ? new Date(interview.data.datetime)
      : interview.data.date
      ? new Date(interview.data.date)
      : new Date(0);
  }
  return new Date(0);
}

function getInterviewLevel(interview) {
  if (interview.type === "video") return interview.level || "N/A";
  return interview.isCompleted ? interview.level || "N/A" : interview.data?.level || "N/A";
}


function getInterviewScore(interview) {
  if (interview.type === "video") {
    return typeof interview.avg_score === "number" ? Number(interview.avg_score).toFixed(1) : "N/A";
  }
  if (interview.isCompleted) {
    if (interview.rating && Array.isArray(interview.rating) && interview.rating.length > 0) {
      const avg = interview.rating.reduce((sum, score) => sum + score, 0) / interview.rating.length;
      return avg.toFixed(1);
    }
    if (typeof interview.avg_score === "number") return Number(interview.avg_score).toFixed(1);
    return "N/A";
  }
  return "0";
}

/* ---------- video interview feedback (same fetches as legacy Feedback component) ---------- */
function VideoFeedback({ docId }) {
  const [feedback, setFeedback] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    let alive = true;
    const fetchFeedback = async () => {
      try {
        const response = await fetch(FEEDBACK_API, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "Get_Feedback", doc_id: docId }),
        });
        if (response.ok) {
          const data = await response.json();
          if (alive) setFeedback(data);
        } else {
          console.error("Error fetching feedback", await response.text());
          if (alive) setError("We could not load the feedback for this interview.");
        }
      } catch (err) {
        console.error("Network error:", err);
        if (alive) setError("We could not load the feedback for this interview.");
      } finally {
        if (alive) setLoading(false);
      }
    };
    fetchFeedback();
    return () => {
      alive = false;
    };
  }, [docId]);

  const downloadReport = async () => {
    setDownloading(true);
    try {
      const response = await fetch(FEEDBACK_DOC_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "Get_Feedback_doc", doc_id: docId }),
      });
      if (response.ok) {
        const data = await response.json();
        if (data.pdf_data) {
          const byteCharacters = atob(data.pdf_data);
          const byteNumbers = Array.from(byteCharacters).map((c) => c.charCodeAt(0));
          const blob = new Blob([new Uint8Array(byteNumbers)], { type: "application/pdf" });
          const now = new Date();
          const stamp = `${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}-${now.getFullYear()}_${String(now.getHours()).padStart(2, "0")}-${String(now.getMinutes()).padStart(2, "0")}-${String(now.getSeconds()).padStart(2, "0")}`;
          const link = document.createElement("a");
          link.href = URL.createObjectURL(blob);
          link.download = `interview_report_${stamp}.pdf`;
          link.click();
          URL.revokeObjectURL(link.href);
        } else {
          toast.error("The report is not available yet. Please try again later.");
        }
      } else {
        console.error("Error fetching feedback", await response.text());
        toast.error("Could not download the analysis report.");
      }
    } catch (err) {
      console.error("Network error:", err);
      toast.error("Could not download the analysis report.");
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="stack-3">
        <Skel h={20} w="45%" />
        <Skel h={10} />
        <Skel h={10} w="80%" />
        <Skel h={90} />
      </div>
    );
  }
  if (error) return <Note tone="danger">{error}</Note>;
  if (!feedback || Object.keys(feedback).length === 0) {
    return <Empty icon={<LuVideo size={22} />} title="No feedback available" sub="This interview has no AI feedback yet." />;
  }

  const overall = Number(feedback.overall_score || 85);
  return (
    <div className="stack-4">
      <div>
        <div className="row-between mb-1">
          <span className="t-sm t-ink" style={{ fontWeight: 600 }}>
            Overall performance
          </span>
          <span className="t-h3 t-accent">{overall}%</span>
        </div>
        <div className="bar">
          <span style={{ width: `${Math.max(0, Math.min(100, overall))}%` }} />
        </div>
      </div>

      {(feedback.strengths || []).length > 0 ? (
        <div>
          <div className="ip-minihead">Your strengths</div>
          <ul className="stack-2">
            {(feedback.strengths || []).map((item, i) => (
              <li key={i} className="row t-sm" style={{ alignItems: "flex-start" }}>
                <LuCheck size={15} style={{ color: "var(--accent-strong)", marginTop: 3, flex: "0 0 auto" }} />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {(feedback.areas_for_improvement || []).length > 0 ? (
        <div>
          <div className="ip-minihead">Areas for improvement</div>
          <ul className="stack-2">
            {(feedback.areas_for_improvement || []).map((item, i) => (
              <li key={i} className="row t-sm" style={{ alignItems: "flex-start" }}>
                <LuTrendingUp size={15} style={{ color: "var(--amber)", marginTop: 3, flex: "0 0 auto" }} />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {Object.keys(feedback.ratings || {}).length > 0 ? (
        <div>
          <div className="ip-minihead">Performance metrics</div>
          <div className="stack-3">
            {Object.entries(feedback.ratings || {}).map(([key, value]) => (
              <div key={key}>
                <div className="row-between mb-1">
                  <span className="t-sm" style={{ textTransform: "capitalize" }}>
                    {key.replace(/_/g, " ")}
                  </span>
                  <span className="t-sm t-ink" style={{ fontWeight: 600 }}>
                    {value}/10
                  </span>
                </div>
                <div className="bar">
                  <span style={{ width: `${(Number(value) / 10) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {(feedback.analysis_highlights || []).length > 0 ? (
        <div>
          <div className="ip-minihead">Key insights</div>
          <ol className="stack-2" style={{ listStyle: "decimal", paddingLeft: 18 }}>
            {(feedback.analysis_highlights || []).map((item, i) => (
              <li key={i} className="t-sm">
                {item}
              </li>
            ))}
          </ol>
        </div>
      ) : null}

      <div>
        <Btn variant="ghost" className="btn-pill" onClick={downloadReport} loading={downloading}>
          <LuDownload size={16} /> Download analysis report
        </Btn>
      </div>
    </div>
  );
}

/* ============================================================ */
export default function InterviewPrep() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userEmail = useSelector((state) => state.auth.auth.email);
  const {
    featuresToBlock = [],
    apiToken,
    apiTokenReady,
    scenarioBased,
    interviewLevel,
    industryInterview,
  } = useSelector((state) => state.profile);

  const [interviews, setInterviews] = useState([]);
  const [incompleteInterviews, setIncompleteInterviews] = useState([]);
  const [videoInterviewData, setVideoInterviewData] = useState([]);
  const [tips, setTips] = useState([]);
  const [showLoader, setShowLoader] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const [startInterviewType, setStartInterviewType] = useState("text-based");
  const [levelError, setLevelError] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [tipsOpen, setTipsOpen] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [busy, setBusy] = useState("");

  /* ---------- fetches (payloads identical to legacy) ---------- */
  const fetchInterviews = async () => {
    const payload = { email: userEmail, task: "fetch_all_interviews" };
    try {
      const response = await fetch(IP_API, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${apiToken}` },
        body: JSON.stringify(payload),
      });
      if (response.ok) {
        const data = await response.json();
        setInterviews(data.response || []);
        if (data.video_interview) setVideoInterviewData(data.video_interview);
        setLoadError("");
      } else {
        console.error("Error fetching interviews");
        setLoadError("We could not load your interview history.");
      }
    } catch (error) {
      console.error("Error:", error);
      setLoadError("We could not load your interview history.");
    } finally {
      setShowLoader(false);
    }
  };

  const fetchIncompleteInterviews = async () => {
    const payload = { email: userEmail, task: "fetch_incomplete_interviews" };
    try {
      const response = await fetch(IP_API, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${apiToken}` },
        body: JSON.stringify(payload),
      });
      if (response.ok) {
        const data = await response.json();
        setIncompleteInterviews(data.response || []);
      } else {
        console.error("Error fetching interviews");
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const fetchTips = async () => {
    const payload = { email: userEmail, task: "fetch_tips" };
    try {
      const response = await fetch(IP_API, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${apiToken}` },
        body: JSON.stringify(payload),
      });
      if (response.ok) {
        const data = await response.json();
        setTips(data.response[0] || []);
      } else {
        console.error("Error fetching interviews");
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  useEffect(() => {
    if (apiToken !== "" && userEmail !== "") {
      const t = setTimeout(() => {
        fetchInterviews();
        fetchTips();
        fetchIncompleteInterviews();
      }, 1000);
      dispatch(setIndustryInterview(""));
      dispatch(setInterviewLevel(""));
      dispatch(setScenarioBased(false));
      return () => clearTimeout(t);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiTokenReady]);

  const retryLoad = () => {
    setShowLoader(true);
    setLoadError("");
    fetchInterviews();
    fetchTips();
    fetchIncompleteInterviews();
  };

  /* ---------- start / create (dispatches + timings identical to legacy) ---------- */
  const startExam = (interview) => {
    setBusy("Loading your interview…");
    dispatch(setQuestionAnswer(interview.data.questions));
    dispatch(setDocId(interview.id));
    dispatch(setAnswers(interview.data.answers));
    localStorage.setItem("examSource", "interview-prep"); // label/back-nav context (#3)
    setTimeout(() => {
      navigate("/interview-exam");
    }, 6000);
  };

  const CreateExam = async () => {
    setBusy("Generating your interview questions…");
    const payLoad = {
      email: userEmail,
      level: interviewLevel,
      scenario_based: scenarioBased,
      task: "create_exam",
    };
    try {
      const response = await fetch(IP_API, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${apiToken}` },
        body: JSON.stringify(payLoad),
      });
      if (response.ok) {
        const data = await response.json();
        dispatch(setQuestionAnswer(data.response));
        dispatch(setDocId(data.doc_id));
        dispatch(setAnswers(Array((data.response || []).length).fill(""))); // fresh exam starts empty (#2)
        localStorage.setItem("examSource", "interview-prep"); // label/back-nav context (#3)
        setTimeout(() => {
          navigate("/interview-exam");
        }, 11000);
      } else {
        console.error("Error creating exam:", await response.text());
        toast.error("Failed to create exam. Please try again.");
        setBusy("");
      }
    } catch (error) {
      console.error("Error creating exam:", error);
      toast.error("Error creating exam: " + error);
      setBusy("");
    }
  };

  /* ---------- featuresToBlock gating (conditions identical to legacy) ---------- */
  const createBlocked =
    featuresToBlock.includes("block_preptests_free") ||
    featuresToBlock.includes("block_preptests_paid") ||
    featuresToBlock.includes("block_preptests");

  const handleSubmit = (e) => {
    e.preventDefault();
    if (createBlocked) {
      setUpgradeOpen(true);
      return;
    }
    if (!interviewLevel) {
      setLevelError(true);
      return;
    }
    setLevelError(false);
    CreateExam();
  };

  function goToVideoInterviewPage() {
    navigate("/videoInterview");
  }

  const onVideoStart = () => {
    if (createBlocked) {
      setUpgradeOpen(true);
      return;
    }
    goToVideoInterviewPage();
  };

  function makePayment() {
    setUpgradeOpen(false);
    dispatch(setShowPro(true));
    navigate("/subscription");
  }

  function jobNavigate() {
    navigate("/search-job");
  }

  /* ---------- combine + paginate ----------
     The two source lists carry `completed` at different depths — the legacy
     code checked `item.completed` on one and `item.data.completed` on the
     other, so a record that matched neither shape was dropped from the table
     entirely and nothing ever read as Completed. Read both depths, keep each
     record once, and let the flag decide the badge. */
  /* NOTE: every row here reads "In progress" while Aspire Quest reports ~89 of
     the same sessions completed. Tried widening these filters — it only
     surfaced 92 malformed rows (N/A date, N/A level) that were being excluded
     on purpose, so the completed records are genuinely absent from both arrays
     this page fetches. It is a data/endpoint mismatch, not a filter bug; fixing
     it needs the interview_prep_function_v2 response reconciled with the one
     Aspire Quest reads. Left as-is deliberately. */
  const combinedInterviews = [
    ...interviews.filter((item) => item.completed === true).map((item) => ({ ...item, isCompleted: true })),
    ...incompleteInterviews
      .filter((item) => item.data?.completed === false)
      .map((item) => ({ ...item, isCompleted: false })),
    ...videoInterviewData.map((item) => ({ ...item, type: "video" })),
  ].sort((a, b) => {
    const dateA = new Date(a.data?.datetime || a.datetime || 0);
    const dateB = new Date(b.data?.datetime || b.datetime || 0);
    return dateB - dateA;
  });

  const totalRecords = combinedInterviews.length;
  const totalPages = Math.ceil(totalRecords / RECORDS_PER_PAGE);
  const paginatedInterviews = combinedInterviews
    .slice((currentPage - 1) * RECORDS_PER_PAGE, currentPage * RECORDS_PER_PAGE)
    .sort((a, b) => getInterviewDate(b) - getInterviewDate(a));

  const hasHistory = interviews.length > 1 || videoInterviewData.length > 0;
  const lastInterview = interviews[interviews.length - 1];

  /* The aggregate fields (overall_avg_rating/max_score/min_score) aren't
     populated on the interview objects, so the score boxes showed 0% / "not
     evaluated" even when interviews were scored (Pavithra Interview Prep #1/#4).
     Compute the summary from each completed interview's ratings (0–10 → ×10 %). */
  const scoredAvgs = interviews
    .map((iv) => {
      if (Array.isArray(iv?.rating) && iv.rating.length) {
        return iv.rating.reduce((s, r) => s + Number(r || 0), 0) / iv.rating.length;
      }
      if (typeof iv?.avg_score === "number") return iv.avg_score;
      return null;
    })
    .filter((v) => typeof v === "number" && !Number.isNaN(v));
  const hasScores = scoredAvgs.length > 0;
  const overallScorePct = hasScores ? ((scoredAvgs.reduce((s, v) => s + v, 0) / scoredAvgs.length) * 10).toFixed(1) : "0";
  const highestScorePct = hasScores ? (Math.max(...scoredAvgs) * 10).toFixed(1) : "0";
  const minScorePct = hasScores ? (Math.min(...scoredAvgs) * 10).toFixed(1) : "0";

  /* ============================ render ============================ */
  return (
    <div className="ip-layout">
      {/* ---------- left column: start a new prep session (create-exam flow), sticky on desktop ---------- */}
      <section className="ld-glass ip-panel ip-side">
        <h2 className="t-h2">Start a new prep session</h2>
        <p className="t-sm mt-1">
          Pick a difficulty, whether you want scenario-based questions, and your target company or industry.
        </p>

        <div className="row mt-3" style={{ gap: 8, flexWrap: "wrap" }}>
          <button
            type="button"
            className={`chip${startInterviewType === "text-based" ? " on" : ""}`}
            onClick={() => setStartInterviewType("text-based")}
          >
            <LuMessageSquare size={14} /> Text-based interview
          </button>
          <button
            type="button"
            className={`chip${startInterviewType === "video-based" ? " on" : ""}`}
            onClick={() => setStartInterviewType("video-based")}
          >
            <LuVideo size={14} /> Video-based interview
          </button>
        </div>

        <form onSubmit={handleSubmit} className="ip-controls">
          <div className="ip-controls-row">
            <div className="ip-seg" role="group" aria-label="Interview level">
              {LEVELS.map((l) => (
                <button
                  key={l}
                  type="button"
                  className={`ip-seg-btn${interviewLevel === l ? " on" : ""}`}
                  onClick={() => {
                    dispatch(setInterviewLevel(l));
                    setLevelError(false);
                  }}
                >
                  {l}
                </button>
              ))}
            </div>

            <div className="ip-switch-wrap">
              <button
                type="button"
                role="switch"
                aria-checked={scenarioBased}
                aria-label="Scenario-based questions"
                className={`ip-switch${scenarioBased ? " on" : ""}`}
                onClick={() => dispatch(setScenarioBased(!scenarioBased))}
              />
              <span className="t-sm">Scenario-based</span>
            </div>
          </div>

          <Field label="Company / industry">
            <input
              className="input"
              placeholder="Type your preferred company or industry"
              value={industryInterview}
              onChange={(e) => dispatch(setIndustryInterview(e.target.value))}
              required
            />
          </Field>

          {levelError ? <Note tone="danger">Pick an interview level to continue.</Note> : null}

          <div className="row" style={{ gap: 10, flexWrap: "wrap" }}>
            {startInterviewType === "text-based" ? (
              <Btn variant="mint" className="btn-pill" type="submit">
                <LuPlay size={15} /> Start session
              </Btn>
            ) : (
              <Btn variant="mint" className="btn-pill" type="button" onClick={onVideoStart}>
                <LuVideo size={15} /> Go to video interview
              </Btn>
            )}
          </div>
        </form>
      </section>

      {/* ---------- right column: stats + session history ---------- */}
      <div className="ip-maincol stack-4">
      {loadError ? (
        <Note tone="danger">
          <span className="grow">{loadError}</span>
          <button type="button" onClick={retryLoad} style={{ fontWeight: 600, textDecoration: "underline" }}>
            Retry
          </button>
        </Note>
      ) : null}

      {/* ---------- score summary — formulas identical to legacy ---------- */}
      {showLoader ? (
        <div className="grid-4">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="ld-glass ip-stat">
              <Skel h={40} w={40} r={14} />
              <Skel h={12} w="60%" style={{ marginTop: 12 }} />
              <Skel h={26} w="45%" style={{ marginTop: 8 }} />
            </div>
          ))}
        </div>
      ) : hasHistory ? (
        <div className="grid-4">
          <div className="ld-glass ld-tile-hover ip-stat">
            <span className="ld-stat-icon">
              <LuTrophy size={18} />
            </span>
            <span className="ld-eyebrow">Your overall score</span>
            <span className="ld-stat-n text-mint">{overallScorePct} %</span>
            <span className="t-xs">{hasScores ? "Across your completed interviews" : "Score not evaluated yet"}</span>
          </div>
          <div className="ld-glass ld-tile-hover ip-stat">
            <span className="ld-stat-icon">
              <LuTrendingUp size={18} />
            </span>
            <span className="ld-eyebrow">Your highest score</span>
            <span className="ld-stat-n text-mint">{highestScorePct} %</span>
            <span className="t-xs">{hasScores ? "Your best interview so far" : "Score not evaluated yet"}</span>
          </div>
          <div className="ld-glass ld-tile-hover ip-stat">
            <span className="ld-stat-icon">
              <LuTrendingDown size={18} />
            </span>
            <span className="ld-eyebrow">Your minimum score</span>
            <span className="ld-stat-n text-mint">{minScorePct} %</span>
            <span className="t-xs">{hasScores ? "Room to grow from here" : "Score not evaluated yet"}</span>
          </div>
          <button type="button" className="ld-glass ld-tile-hover ip-stat ip-stat-btn" onClick={() => setTipsOpen(true)}>
            <span className="ld-stat-icon">
              <LuLightbulb size={18} />
            </span>
            <span className="ld-eyebrow">Interview exam tips</span>
            <span className="ld-stat-n text-mint">{Object.keys(tips || {}).length || 4}</span>
            <span className="t-xs">Get insights of exams</span>
          </button>
        </div>
      ) : null}

      {/* ---------- session history ---------- */}
      <section className="ld-glass ip-panel">
        <div className="row-between" style={{ flexWrap: "wrap", gap: 10 }}>
          <h2 className="t-h2">Session history</h2>
          {hasHistory && !showLoader ? (
            <span className="t-xs">
              {totalRecords} session{totalRecords === 1 ? "" : "s"}
            </span>
          ) : null}
        </div>

        {showLoader ? (
          <div className="stack-2 mt-3">
            {[0, 1, 2, 3, 4].map((i) => (
              <Skel key={i} h={52} r={18} />
            ))}
          </div>
        ) : !hasHistory ? (
          <div className="ld-empty mt-3">
            <span className="ld-empty-icon">
              <LuNotebookPen size={18} />
            </span>
            <div className="ld-empty-title">No sessions yet</div>
            <p className="ld-empty-sub">
              Run a prep round above and it will appear here with per-question feedback.
            </p>
          </div>
        ) : (
          <>
            <ul className="ip-rows">
              {paginatedInterviews.map((interview, index) => {
                const key = interview.id || `row-${index}`;
                const open = openId === key;
                const interviewDate = getInterviewDate(interview);
                const isVideoRow = interview.status === "completed" || interview.status === "in_progress";
                const score = getInterviewScore(interview);
                return (
                  <li key={key} className="ip-rowitem">
                    <button
                      type="button"
                      className="ip-rowhead"
                      aria-expanded={open}
                      onClick={() => setOpenId(open ? null : key)}
                    >
                      <span className="ip-rowdate">
                        {interviewDate.getTime() !== 0
                          ? interviewDate.toLocaleDateString(undefined, {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            })
                          : "N/A"}
                      </span>
                      <span className="ip-lvl">{getInterviewLevel(interview)}</span>
                      {isVideoRow && !interview.isCompleted ? (
                        <Badge tone="info">
                          <LuVideo size={12} /> Video
                        </Badge>
                      ) : null}
                      {/* this column repeated the signed-in user's own address on
                          every row; the format is the thing you actually scan for */}
                      <span className="t-xs ip-cell-trunc">
                        {isVideoRow || interview.type === "video" ? "Video interview" : "Text interview"}
                      </span>
                      <span className="ip-rowend">
                        {interview.isCompleted || isVideoRow ? (
                          <span className="ip-score">
                            <LuStar size={14} /> {score}
                          </span>
                        ) : (
                          <Badge tone="amber">In progress</Badge>
                        )}
                        <LuChevronDown size={16} className={`ip-chev${open ? " open" : ""}`} />
                      </span>
                    </button>

                    {open ? (
                      <div className="ip-rowbody">
                        {interview.isCompleted ? (
                          (interview.questions || []).length === 0 ? (
                            <p className="t-sm">No per-question feedback was recorded for this session.</p>
                          ) : (
                            (interview.questions || []).map((q, qi) => {
                              const r = (interview.rating || [])[qi];
                              return (
                                <div key={qi} className="ip-qacard">
                                  <div className="ip-qahead">
                                    <p className="ip-q">
                                      Question {qi + 1}: {interview.questions[qi][qi + 1] || "N/A"}
                                    </p>
                                    <span className="ip-rate">
                                      {r !== undefined && r !== null && r !== "" ? `${r}/10` : "N/A"}
                                    </span>
                                  </div>
                                  <p className="t-sm mt-1">{(interview.answers || [])[qi] || "N/A"}</p>
                                  <div className="mt-2">
                                    <p className="ip-minihead">Feedback</p>
                                    <p className="t-sm">{(interview.feedback_comments || [])[qi] || "N/A"}</p>
                                  </div>
                                </div>
                              );
                            })
                          )
                        ) : isVideoRow ? (
                          <VideoFeedback docId={interview.id} />
                        ) : (
                          <div className="row-between" style={{ flexWrap: "wrap", gap: 10 }}>
                            <p className="t-sm">This session was never finished — resume it to get scored feedback.</p>
                            <Btn variant="mint" className="btn-pill" size="sm" onClick={() => startExam(interview)}>
                              <LuPlay size={14} /> Resume
                            </Btn>
                          </div>
                        )}
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>

            <div className="row mt-3" style={{ gap: 6, flexWrap: "wrap" }}>
              <Btn
                variant="ghost"
                className="btn-pill"
                size="sm"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(1)}
                aria-label="First page"
              >
                <LuChevronsLeft size={15} />
              </Btn>
              <Btn
                variant="ghost"
                className="btn-pill"
                size="sm"
                disabled={currentPage === 1}
                onClick={() => currentPage > 1 && setCurrentPage(currentPage - 1)}
                aria-label="Previous page"
              >
                <LuChevronLeft size={15} />
              </Btn>
              <span className="t-sm" style={{ padding: "0 6px" }}>
                Page {totalPages === 0 ? 0 : currentPage} of {totalPages}
              </span>
              <Btn
                variant="ghost"
                className="btn-pill"
                size="sm"
                disabled={currentPage >= totalPages}
                onClick={() => currentPage < totalPages && setCurrentPage(currentPage + 1)}
                aria-label="Next page"
              >
                <LuChevronRight size={15} />
              </Btn>
              <Btn
                variant="ghost"
                className="btn-pill"
                size="sm"
                disabled={currentPage >= totalPages}
                onClick={() => currentPage < totalPages && setCurrentPage(totalPages)}
                aria-label="Last page"
              >
                <LuChevronsRight size={15} />
              </Btn>
            </div>
          </>
        )}
      </section>
      </div>

      {/* ---------- tips dialog ---------- */}
      <Dialog open={tipsOpen} onClose={() => setTipsOpen(false)}>
        <div className="row-between mb-2">
          <div className="t-h3">Tips for interview exam</div>
          <button type="button" onClick={() => setTipsOpen(false)} aria-label="Close" style={{ color: "var(--ink-3)" }}>
            <LuX size={18} />
          </button>
        </div>
        <div className="stack-2">
          {Object.entries(tips || {}).map(([key, value]) => (
            <div key={key} className="row t-sm" style={{ alignItems: "flex-start" }}>
              <LuLightbulb size={15} style={{ color: "var(--amber)", marginTop: 3, flex: "0 0 auto" }} />
              <span>
                {key}. {value}
              </span>
            </div>
          ))}
          {Object.keys(tips || {}).length === 0 ? <p className="t-sm">No tips available right now.</p> : null}
        </div>
      </Dialog>

      {/* ---------- upgrade reminder (gating copy identical to legacy) ---------- */}
      <Dialog open={upgradeOpen} onClose={() => setUpgradeOpen(false)}>
        <div className="ip-upgrade">
          <div className="ip-upgrade-glow" aria-hidden="true" />
          <button
            type="button"
            className="ip-upgrade-x"
            onClick={() => setUpgradeOpen(false)}
            aria-label="Close"
          >
            <LuX size={18} />
          </button>
          <span className="ip-upgrade-lock">
            <LuLock size={18} />
          </span>
          <h3 className="t-h3 mt-2">Upgrade to Pro</h3>
          <p className="t-sm mt-1">
            {featuresToBlock.includes("block_preptests_free")
              ? "Upgrade to Pro to access these features and reach your career goals faster with the help of Career Savvy."
              : "You have used the allowable tests for this month. New tests will reset at the beginning of the next subscription cycle."}
          </p>
          <div className="mt-3">
            {featuresToBlock.includes("block_preptests_free") ? (
              <Btn variant="mint" className="btn-pill" onClick={makePayment}>
                <LuSparkles size={15} /> Upgrade now
              </Btn>
            ) : (
              <Btn variant="mint" className="btn-pill" onClick={jobNavigate}>
                Search jobs
              </Btn>
            )}
          </div>
        </div>
      </Dialog>

      {/* ---------- busy overlay (create / resume in flight) ---------- */}
      {busy ? (
        <div className="ip-scrim">
          <div className="ip-busy">
            <Spinner lg />
            <div className="t-sm t-ink mt-2" style={{ fontWeight: 600 }}>
              {busy}
            </div>
            <div className="t-xs mt-1">This can take a moment — hang tight.</div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
